import { useId, useState } from 'react'
import { ChevronRight } from 'lucide-react'
import {
  formatPlaceCount,
  type TClass,
  type TDivision,
  type TShow,
  type UpdateClassFinalizationInput,
} from '@schemas'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import RulesViewer from '@/components/catalog/RulesViewer'
import { mergeCatalogRules } from '@/lib/catalog'
import { getCategoryBadgeClass } from '@/lib/classColors'
import { resolveLabelsPerExhibitFromClass } from '@/lib/labels-per-exhibit'
import { cn } from '@/lib/utils'

export type DivisionWithClasses = TDivision & {
  classes: Array<
    TClass & {
      catalogClass?: {
        id: string
        name: string
        categoryCode: string
        categoryName: string
        flags?: Record<string, unknown> | string
      }
    }
  >
}

export type LabelsEditorState = Record<string, { value: string; dirty: boolean; display: number }>

type Props = {
  divisions: DivisionWithClasses[]
  show: TShow
  labelsEditor: LabelsEditorState
  savingClassId: string | null
  classDisabled: boolean
  classReason?: string
  resultsDisabled: boolean
  resultsReason?: string
  updatingClass: boolean
  updatingResults: boolean
  onLabelsChange: (id: string, value: string, resolved: number) => void
  onLabelsSave: (showClass: TClass, resolved: number) => void
  onGrandChampionChange: (id: string, excluded: boolean, label: string) => void
  onResultsChange: (id: string, payload: UpdateClassFinalizationInput) => void
  onEdit: (id: string, trigger: HTMLButtonElement) => void
}

function flagLabels(flags: unknown): string[] {
  const labels =
    typeof flags === 'string'
      ? [flags]
      : flags && typeof flags === 'object' && !Array.isArray(flags)
        ? Object.entries(flags)
            .filter(([, value]) => Boolean(value))
            .map(([key]) => key)
        : []
  return labels.map(label =>
    label
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/^./, char => char.toUpperCase())
      .replace(/\s+(\w)/g, (_, char: string) => ` ${char.toUpperCase()}`)
  )
}

export function ShowClassesOutline(props: Props) {
  const prefix = useId()
  const [expandedClass, setExpandedClass] = useState<string | null>(null)
  // The first division to load starts open. After that each division keeps the state it was
  // given, so a reorder arriving in a background refresh does not collapse the one being edited.
  const firstDivision = props.divisions[0]
  const [divisionOpen, setDivisionOpen] = useState<Record<string, boolean> | null>(null)
  if (divisionOpen === null && firstDivision) {
    setDivisionOpen({ [firstDivision.id ?? firstDivision.code]: true })
  }

  return (
    <div className="space-y-3">
      {props.divisions.map(division => {
        const divisionKey = division.id ?? division.code
        const open = divisionOpen?.[divisionKey] ?? false
        const divisionTriggerId = `${prefix}-${divisionKey}-division`
        const divisionPanelId = `${divisionTriggerId}-panel`
        return (
          <section key={divisionKey} className="rounded-lg border bg-card">
            <div className="relative flex min-h-16 items-start gap-3 p-3 sm:p-4">
              <ChevronRight
                aria-hidden="true"
                className={cn('mt-0.5 size-5 shrink-0', open && 'rotate-90')}
              />
              <div className="min-w-0">
                <h2 className="break-words text-sm font-semibold sm:text-base">
                  {/* The heading holds only the name; the overlay makes the whole header the target. */}
                  <button
                    id={divisionTriggerId}
                    type="button"
                    aria-expanded={open}
                    aria-controls={divisionPanelId}
                    aria-describedby={`${divisionTriggerId}-summary`}
                    onClick={() => setDivisionOpen(prev => ({ ...prev, [divisionKey]: !open }))}
                    className="cursor-pointer text-left after:absolute after:inset-0 after:rounded-lg focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring"
                  >
                    {division.name}
                  </button>
                </h2>
                <p
                  id={`${divisionTriggerId}-summary`}
                  className="mt-0.5 text-xs text-muted-foreground"
                >
                  {division.code} · {division.classes.length}{' '}
                  {division.classes.length === 1 ? 'class' : 'classes'} · Order {division.order}
                </p>
              </div>
            </div>
            <div
              id={divisionPanelId}
              hidden={!open}
              className="space-y-1 px-2 pb-2 sm:space-y-2 sm:px-3 sm:pb-3"
            >
              {division.classes.length === 0 && (
                <p className="p-3 text-sm text-muted-foreground">
                  No classes in this division yet.
                </p>
              )}
              {division.classes.map(showClass => {
                const classId = showClass.id ?? undefined
                const key = classId ?? `${division.id}-${showClass.code}`
                const panelId = `${prefix}-${key}-panel`
                const triggerId = `${prefix}-${key}-trigger`
                const expanded = expandedClass === key
                const customized = Boolean(
                  showClass.displayName || showClass.ruleOverrides || showClass.parameters
                )
                const resolved = resolveLabelsPerExhibitFromClass(showClass)
                const editor = classId ? props.labelsEditor[classId] : undefined
                const value = editor?.value ?? String(resolved)
                const display = editor?.display ?? resolved
                const parsed = Number.parseInt(value.trim(), 10)
                const valid = value.trim().length > 0 && Number.isFinite(parsed) && parsed >= 1
                const changed = Boolean(editor?.dirty && valid && parsed !== display)
                const saving = Boolean(classId && props.savingClassId === classId)
                const inputId = `${prefix}-${key}-labels`
                const helperId = `${inputId}-helper`
                const excludeId = `${prefix}-${key}-exclude-grand`
                const excluded = showClass.grandChampionEligible === false
                const finalizedAt = showClass.resultsFinalizedAt
                  ? new Date(showClass.resultsFinalizedAt)
                  : null
                const finalized = Boolean(finalizedAt)
                const noAwards = Boolean(showClass.noAwards)
                const resultsPhaseReason =
                  props.show.status === 'finalized'
                    ? 'Reopen the show before changing class results.'
                    : !finalized && props.show.status !== 'judging'
                      ? 'Class results can only be finalized during Judging.'
                      : undefined
                const noAwardsPhaseReason =
                  props.show.status === 'finalized'
                    ? 'Reopen the show before changing class results.'
                    : undefined
                const resultsReason = props.resultsReason ?? resultsPhaseReason
                const noAwardsReason = props.resultsReason ?? noAwardsPhaseReason
                const classLabel = showClass.name ?? showClass.code ?? 'class'
                const statusLabel = noAwards
                  ? 'No awards (excluded)'
                  : finalized
                    ? `Finalized ${finalizedAt?.toLocaleString()}`
                    : 'Awaiting results'
                return (
                  <section
                    key={key}
                    data-testid={`show-class-row-${classId ?? showClass.code}`}
                    className="@container/class-outline min-w-0 rounded-md border"
                  >
                    <h3>
                      <button
                        id={triggerId}
                        type="button"
                        aria-expanded={expanded}
                        aria-controls={panelId}
                        aria-label={`${showClass.code} ${classLabel}`}
                        aria-describedby={`${triggerId}-badges ${triggerId}-labels`}
                        onClick={() => setExpandedClass(expanded ? null : key)}
                        className="flex min-h-16 w-full items-start gap-3 rounded-md p-2 text-left hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:items-center sm:p-4"
                      >
                        <ChevronRight
                          aria-hidden="true"
                          className={cn('mt-0.5 size-5 shrink-0 sm:mt-0', expanded && 'rotate-90')}
                        />
                        <span className="shrink-0 font-semibold">{showClass.code}</span>
                        <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1 @3xl/class-outline:flex @3xl/class-outline:flex-wrap @3xl/class-outline:gap-x-4">
                          <span className="col-span-2 break-words text-sm font-medium sm:text-base">
                            {showClass.name}
                          </span>
                          <span
                            id={`${triggerId}-badges`}
                            className="flex min-w-0 flex-wrap items-center gap-1"
                          >
                            {showClass.catalogClass && (
                              <>
                                <Badge
                                  variant="secondary"
                                  className={getCategoryBadgeClass(
                                    showClass.catalogClass.categoryCode
                                  )}
                                >
                                  {showClass.catalogClass.categoryCode}
                                </Badge>
                                {flagLabels(showClass.catalogClass.flags).map(flag => (
                                  <Badge
                                    key={flag}
                                    variant="outline"
                                    className="max-w-full whitespace-normal"
                                  >
                                    {flag}
                                  </Badge>
                                ))}
                              </>
                            )}
                            <Badge variant="secondary">
                              {customized ? 'Customized' : 'From Catalog'}
                            </Badge>
                            {excluded && (
                              <Badge variant="destructive" className="max-w-full whitespace-normal">
                                Excluded from Grand Champion
                              </Badge>
                            )}
                            {showClass.placeCount != null && (
                              <Badge variant="outline">
                                {formatPlaceCount(showClass.placeCount)}
                              </Badge>
                            )}
                            {noAwards && <Badge variant="outline">No awards</Badge>}
                            {finalized && <Badge variant="outline">Finalized</Badge>}
                          </span>
                          <span
                            id={`${triggerId}-labels`}
                            className="text-right text-[11px] font-normal text-muted-foreground @3xl/class-outline:ml-auto @3xl/class-outline:shrink-0 sm:text-sm"
                          >
                            {display} {display === 1 ? 'label' : 'labels'}/exhibit
                          </span>
                        </span>
                      </button>
                    </h3>
                    <div
                      id={panelId}
                      role="region"
                      aria-labelledby={triggerId}
                      hidden={!expanded}
                      className="border-t bg-muted/15 p-3 sm:p-5"
                    >
                      {expanded && (
                        <div className="grid min-w-0 gap-4 @3xl/class-outline:grid-cols-2 @3xl/class-outline:gap-6">
                          <div className="min-w-0 space-y-3 @3xl/class-outline:space-y-4">
                            <h4 className="text-sm font-semibold">Class settings</h4>
                            {!classId ? (
                              <p className="text-sm text-muted-foreground">
                                Save the class before editing.
                              </p>
                            ) : (
                              <form
                                className="space-y-1 @3xl/class-outline:space-y-2"
                                onSubmit={event => {
                                  event.preventDefault()
                                  props.onLabelsSave(showClass, resolved)
                                }}
                              >
                                <Label htmlFor={inputId} className="text-sm">
                                  Labels required
                                  <span className="sr-only">
                                    {' for '}
                                    {classLabel}
                                  </span>
                                </Label>
                                <div className="flex flex-wrap items-start gap-2">
                                  <div className="min-w-0 space-y-1">
                                    <Input
                                      id={inputId}
                                      aria-label={`Labels required for ${classLabel}`}
                                      type="number"
                                      min={1}
                                      step={1}
                                      inputMode="numeric"
                                      value={value}
                                      onChange={event =>
                                        props.onLabelsChange(classId, event.target.value, resolved)
                                      }
                                      disabled={saving || props.classDisabled}
                                      className="h-11 w-20"
                                      aria-invalid={Boolean(editor?.dirty && !valid)}
                                      aria-describedby={
                                        editor?.dirty && !valid ? helperId : undefined
                                      }
                                    />
                                    {editor?.dirty && !valid && (
                                      <p id={helperId} className="text-xs text-destructive">
                                        Enter 1 or greater.
                                      </p>
                                    )}
                                  </div>
                                  <span className="py-3 text-xs text-muted-foreground">
                                    labels/exhibit
                                  </span>
                                  <Button
                                    type="submit"
                                    variant="outline"
                                    className="min-h-11 w-full @sm/class-outline:ml-auto @sm/class-outline:w-auto"
                                    disabled={!changed || saving || props.classDisabled}
                                    aria-label={`Save labels required for ${classLabel}`}
                                  >
                                    {saving ? 'Saving…' : 'Save labels'}
                                  </Button>
                                </div>
                              </form>
                            )}
                            <div className="space-y-1">
                              <Label
                                htmlFor={excludeId}
                                className="flex min-h-11 cursor-pointer items-center gap-3 text-sm leading-snug"
                              >
                                <input
                                  id={excludeId}
                                  type="checkbox"
                                  className="size-5 shrink-0 rounded border"
                                  checked={excluded}
                                  onChange={event => {
                                    if (classId)
                                      props.onGrandChampionChange(
                                        classId,
                                        event.target.checked,
                                        classLabel
                                      )
                                  }}
                                  disabled={
                                    !classId ||
                                    !props.show.id ||
                                    props.classDisabled ||
                                    props.updatingClass
                                  }
                                  title={props.classReason}
                                  aria-label={`Exclude ${classLabel} from Grand Champion`}
                                />
                                Exclude from Grand Champion
                              </Label>
                              <p className="pl-8 text-xs text-muted-foreground">
                                {excluded
                                  ? 'Grand Champion leaderboards skip this class.'
                                  : 'If checked, this class is excluded from Grand Champion.'}
                              </p>
                            </div>
                            {props.classReason && (
                              <p className="text-xs text-muted-foreground">{props.classReason}</p>
                            )}
                            <details className="group/rules border-t pt-2">
                              <summary className="flex min-h-11 cursor-pointer list-none items-center gap-2 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
                                <ChevronRight
                                  aria-hidden="true"
                                  className="size-4 shrink-0 group-open/rules:rotate-90"
                                />
                                <span>
                                  <span className="block text-sm font-medium">Details</span>
                                  <span className="text-xs text-muted-foreground">
                                    Catalog reference and class rules
                                  </span>
                                </span>
                              </summary>
                              <div className="min-w-0 space-y-3 pt-3 text-sm">
                                {showClass.catalogClass ? (
                                  <div className="break-words text-muted-foreground">
                                    <p className="font-medium">{showClass.catalogClass.name}</p>
                                    <p>{showClass.catalogClass.categoryName}</p>
                                  </div>
                                ) : (
                                  <p className="text-muted-foreground">No catalog reference.</p>
                                )}
                                <RulesViewer
                                  rules={mergeCatalogRules(
                                    showClass.catalogClass?.rules ?? showClass.rules ?? {},
                                    showClass.ruleOverrides ?? {}
                                  )}
                                />
                                <Button
                                  variant="outline"
                                  className="min-h-11 w-full @sm/class-outline:w-auto"
                                  disabled={!classId || props.classDisabled}
                                  title={props.classReason}
                                  onClick={event => {
                                    if (classId) props.onEdit(classId, event.currentTarget)
                                  }}
                                >
                                  Edit
                                </Button>
                              </div>
                            </details>
                          </div>
                          <div className="min-w-0 space-y-2 border-t pt-3 @3xl/class-outline:border-t-0 @3xl/class-outline:border-l @3xl/class-outline:pt-0 @3xl/class-outline:pl-6">
                            <h4 className="text-sm font-semibold">Results</h4>
                            <div className="space-y-1 text-sm">
                              <p>{statusLabel}</p>
                              <p className="text-xs text-muted-foreground">
                                {excluded
                                  ? 'Excluded from Grand Champion awards.'
                                  : 'Eligible for Grand Champion awards.'}
                              </p>
                            </div>
                            <div className="space-y-2">
                              <Button
                                variant={finalized ? 'secondary' : 'outline'}
                                className="min-h-11 w-full whitespace-normal"
                                disabled={
                                  !classId ||
                                  props.updatingResults ||
                                  props.resultsDisabled ||
                                  Boolean(resultsPhaseReason)
                                }
                                title={resultsReason}
                                onClick={() => {
                                  if (!classId) return
                                  if (
                                    !finalized &&
                                    noAwards &&
                                    !window.confirm(
                                      `Finalize ${classLabel} without awards? Its saved placements will be permanently removed.`
                                    )
                                  )
                                    return
                                  props.onResultsChange(classId, { finalized: !finalized })
                                }}
                              >
                                {finalized ? 'Reopen Results' : 'Mark Finalized'}
                              </Button>
                              {resultsReason && (
                                <p className="text-xs text-muted-foreground">
                                  {resultsReason === resultsPhaseReason &&
                                  props.show.status !== 'finalized'
                                    ? 'Only available during Judging.'
                                    : resultsReason}
                                </p>
                              )}
                              <Button
                                variant={noAwards ? 'default' : 'outline'}
                                className="min-h-11 w-full whitespace-normal"
                                disabled={
                                  !classId ||
                                  props.updatingResults ||
                                  props.resultsDisabled ||
                                  Boolean(noAwardsPhaseReason)
                                }
                                title={noAwardsReason}
                                onClick={() => {
                                  if (classId)
                                    props.onResultsChange(classId, {
                                      noAwards: !noAwards,
                                      ...(noAwards ? {} : { finalized: false }),
                                    })
                                }}
                              >
                                {noAwards ? 'Clear No Awards' : 'Mark No Awards'}
                              </Button>
                              {noAwardsReason && noAwardsReason !== resultsReason && (
                                <p className="text-xs text-muted-foreground">{noAwardsReason}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </section>
                )
              })}
            </div>
          </section>
        )
      })}
    </div>
  )
}
